import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { RailzButton } from '@railzai/railz-uikit-react';

import { RVFilterTaxBenchmarking, RVReportTypes } from '@railzai/railz-visualizations';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { createFilterOptions } from '@material-ui/lab';

import { RailzTaxBenchmarking } from '../../../../../helpers/railz-visualizations-react';

import Config from '../../../../../config';
import { chartOptions } from '../summary-charts-options';

import { getSelectedBusiness } from '../../../../../store/features/business/business.selector';
import { isSandbox, getAccessToken } from '../../../../../store/features/auth';

import naics from '../../../../../assets/jsons/naics.json';

import useStyle from './style';

import { formatFilter } from 'helpers/business-helpers/financial-summary-helper';
import { FormInput, FormSelectAutoComplete } from 'components/form';
import { ASP_SERVICE_TYPE, ServiceType } from 'types';

const TaxBenchmarking = (): JSX.Element => {
  const codes = naics.map((naic) => `${naic['2022 NAICS US   Code']}`);
  const { t } = useTranslation();
  const classes = useStyle();
  // const codeRef = useRef();
  // const regionRef = useRef();
  const [configuration, setConfiguration] = useState(null);
  const [filterInfo, setFilterInfo] = useState(null);
  const token = useSelector(getAccessToken);
  const sandbox = useSelector(isSandbox);
  const selectedBusiness = useSelector(getSelectedBusiness);

  useEffect(() => {
    if (token) {
      setConfiguration({
        token,
        endpoint: Config.FEEDER_SERVER,
        debug: Config.DEBUG,
      });
    }
  }, [token]);

  const validationSchema = yup.object().shape({
    industryCode: yup
      .string()
      .nullable()
      .required('DASHBOARD_TAX_BENCHMARKING_INDUSTRY_CODE_REQUIRED'),
    region: yup.string().trim().nullable(),
  });

  const { handleSubmit, errors, control } = useForm({
    mode: 'onChange',
    defaultValues: { industryCode: '', region: '' },
    resolver: yupResolver(validationSchema),
  });

  const getFirstAccountingConnection = (business): any => {
    const accConnection = business?.connections.find(
      (connection) => ASP_SERVICE_TYPE[connection.serviceName] === ServiceType.ACCOUNTING,
    );
    return accConnection;
  };

  const onSubmitData = (values): void => {
    const accountingConnection = getFirstAccountingConnection(selectedBusiness);
    setFilterInfo({
      industryCode: values.industryCode,
      region: values?.region?.toUpperCase() || null,
      connectionUuid: accountingConnection?.connectionId,
    });
  };
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
  });

  return (
    <Box>
      <p className={classes.filterTitle}>
        {t('DASHBOARD_BUSINESS_TAX_BENCHMARKING_AGAINST').toUpperCase()}:
      </p>
      <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmitData)}>
        <div className={classes.taxBenchmarkingFilterContainer}>
          <Grid item lg={4} md={4} sm={12} xs={12} className={classes.industryCodesDropdown}>
            <div style={{ marginTop: '8px', marginBottom: '2px' }}>
              <FormSelectAutoComplete
                label={t('DASHBOARD_TAX_BENCHMARKING_INDUSTRY_CODE_SELECT')}
                fullWidth
                name="industryCode"
                placeholder={t('DASHBOARD_TAX_BENCHMARKING_INDUSTRY_CODE_SELECT')}
                margin="dense"
                errorobj={errors}
                control={control}
                data-testid="industry-code-input"
                options={codes}
                getOptionLabel={(option: any): string => option || ''}
                renderOption={(option): React.ReactElement => (
                  <React.Fragment>{option}</React.Fragment>
                )}
                filterOptions={filterOptions}
              />
            </div>
          </Grid>
          <Grid item lg={4} md={4} sm={12} xs={12} className={classes.industryCodesDropdown}>
            {/* TO DO: change to input auto complete */}
            <FormInput
              defaultValue=""
              label={t('DASHBOARD_TAX_BENCHMARKING_REGION_SELECT')}
              variant="outlined"
              fullWidth
              // select
              // ref={regionRef}
              name="region"
              placeholder={t('DASHBOARD_TAX_BENCHMARKING_REGION_SELECT')}
              margin="dense"
              errorobj={errors}
              control={control}
              data-testid="region-input"
            />
          </Grid>
          <RailzButton
            onButtonClick={(): any => handleSubmit(onSubmitData)}
            className={classes.applyCtaButton}
            data-testid="webhook-config-submit"
            label={t('DASHBOARD_BUSINESS_LIST_CTA_APPLY')}
          />
        </div>
        {sandbox && (
          <div className={classes.taxBenchmarkingSandboxInfo}>
            {t('DASHBOARD_BUSINESS_TAX_BENCHMARKING_AGAINST_SANDBOX')}
          </div>
        )}
      </form>
      <>
        <RailzTaxBenchmarking
          data-testid="sdk-tax-benchmarking"
          configuration={configuration}
          filter={
            formatFilter({
              ...filterInfo,
              reportType: RVReportTypes.TAX_BENCHMARKING,
            }) as RVFilterTaxBenchmarking
          }
          options={chartOptions}
        />
      </>
    </Box>
  );
};

export default TaxBenchmarking;
